<template>
  <p
    v-if="description"
    class="price-page-description text-body-2 text-secondary--text"
    itemscope
    itemtype="https://schema.org/Product"
  >
    {{ description }}
  </p>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import formatPrice from 'src/functions/formatPrice'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { PriceRangeTuple } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceHeader/types'

const props = defineProps<{
  lpu: Lpu
  townName: string
  currencyAbbreviation: string
  priceRange?: PriceRangeTuple
  priceRangeDoctors?: PriceRangeTuple
  priceRangeMrt?: PriceRangeTuple
  priceRangeKt?: PriceRangeTuple
  priceRangeUzi?: PriceRangeTuple
  priceRangeRentgen?: PriceRangeTuple
  priceRangeUslugi?: PriceRangeTuple
}>()

const lpuName = computed(() => (
  tDynamic(props.lpu, 'name_pred')
  || tDynamic(props.lpu, 'name')
  || props.lpu.name
))

function hasRange(range?: PriceRangeTuple): range is PriceRangeTuple {
  return Boolean(range && range[1])
}

function formatOffer(value: number | string | null | undefined) {
  return formatPrice({
    price: value,
    useFreeTextForZero: true,
  }) || '0'
}

function formatRangeSuffix(
  range: PriceRangeTuple,
  formatValue: (_value: number | string | null | undefined) => string | number,
) {
  return [
    tStatic('от'),
    formatValue(range[0]),
    tStatic('до'),
    formatValue(range[1]),
    `${props.currencyAbbreviation}.`,
  ].join(' ')
}

/**
 * Скрытое SEO-описание прайса (как в legacy description_prices).
 */
const description = computed(() => {
  const paidPart = props.lpu.paid ? '' : ` ${tStatic('на платные услуги')}`
  const segments: string[] = [
    `${tStatic('Цены')}${paidPart} ${tStatic('в')} ${lpuName.value}, ${props.townName}: ${tStatic('медицинские услуги')}`,
  ]

  if (hasRange(props.priceRange)) {
    segments[0] += ` ${formatRangeSuffix(props.priceRange, value => value ?? '')}`
  }

  const extras: Array<{
    label: string
    range?: PriceRangeTuple
    formatValue: (_value: number | string | null | undefined) => string | number
  }> = [
    {
      label: tStatic('в том числе первичный прием врачей'),
      range: props.priceRangeDoctors,
      formatValue: formatOffer,
    },
    {
      label: tStatic('МРТ'),
      range: props.priceRangeMrt,
      formatValue: value => value ?? '',
    },
    {
      label: tStatic('КТ'),
      range: props.priceRangeKt,
      formatValue: value => value ?? '',
    },
    {
      label: tStatic('УЗИ'),
      range: props.priceRangeUzi,
      formatValue: value => value ?? '',
    },
    {
      label: tStatic('рентген'),
      range: props.priceRangeRentgen,
      formatValue: value => value ?? '',
    },
    {
      label: tStatic('лечение'),
      range: props.priceRangeUslugi,
      formatValue: value => value ?? '',
    },
  ]

  extras.forEach(({ label, range, formatValue }) => {
    if (!hasRange(range)) {
      return
    }

    segments.push(`${label} ${formatRangeSuffix(range, formatValue)}`)
  })

  return segments.join(', ')
})
</script>

<style scoped lang="scss">
.price-page-description {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
